<template>
	<BaseLayout :footer-full="true" :include-padding="false" :background-color-class="'bg-color-white'">
		<div class="marketing-site-careers">
			<MarketingPageHeader :header="pageHeader" />
			<div class="image-overlap-section mt-200">
				<div class="container">
					<div class="grid pb-100">
						<div class="col-10-md col-offset-1-md">
							<video
								id="office-video"
								class="office-video"
								muted
								autoplay="true"
								crossorigin="Anonymous"
								loop
								role="presentation"
								webkit-playsinline="true"
								playsinline="true"
								disableRemotePlayback="true"
								:poster="appCloudfrontPath + '/images/marketing-site/careers-altvideo-placeholder.png'"
								preload="auto"
							>
								<source :src="appCloudfrontPath + '/video/Careers-Alternate4.mp4'" type="video/mp4" />
							</video>
						</div>
					</div>
				</div>
			</div>
			<article class="pt-200 pb-100 bg-color-greige">
				<div class="container pt-600">
					<div class="grid">
						<div class="col-8-md col-offset-2-md">
							<p class="delta-md line-height-150">
								While Fundrise is one of the largest online real estate investment platforms, our team
								is still relatively small. That means every member of our team has the opportunity — and
								is expected — to make a significant impact on the future of our business. Ultimately, we
								hope to create an environment where every member of our team is inspired and empowered
								to do the best work of their careers.
							</p>
							<p class="delta-md line-height-150">
								If you’re interested in helping build the future of real estate investing, we’d love for
								you to consider applying for one of our job openings.
							</p>
						</div>
					</div>
				</div>
				<div class="container pt-100 pb-100 pt-300-md pb-300-md">
					<div class="grid accolades display-flex justify-content-center">
						<img
							v-for="(accolade, id) in accolades"
							:key="id"
							class="accolade"
							:src="appCloudfrontPath + accolade.imageUrl"
							:alt="accolade.label"
						/>
					</div>
				</div>
			</article>
			<article>
				<div class="container">
					<div class="grid">
						<div
							class="text-align-center pt-200 pt-400-md pb-200 pb-400-md col-12 col-8-md col-6-lg col-offset-2-md col-offset-3-lg"
						>
							<h2 v-if="numberOfDepartments > 0" class="heading-2 mb-100-md">Current openings</h2>
							<ul v-if="numberOfDepartments > 0" class="pt-200">
								<li
									v-for="(department, departmentsIndex) in departments"
									:key="departmentsIndex"
									:class="{ bb: departmentsIndex < numberOfDepartments - 1 }"
									class="mb-200 pb-100 text-align-left"
								>
									<h3 class="heading-5">{{ department[0] }}</h3>
									<ul>
										<li
											v-for="(listing, index) in department[1]"
											:key="index"
											class="pt-100 pb-100 text-align-left"
										>
											<a :href="listing.url" class="subheading dark">
												{{ listing.title }}
											</a>
											<p v-if="listing.city" class="mt-0">
												{{ listing.city }}
											</p>
										</li>
									</ul>
								</li>
							</ul>
							<BaseLink
								v-if="numberOfDepartments == 0"
								:classes="'button button-primary'"
								:link="leverLink"
							/>
						</div>
					</div>
				</div>
			</article>
		</div>
	</BaseLayout>
</template>

<script setup lang="ts">
import { LandingPageHeader, Link } from 'types/layout';
import { Ref, ref } from 'vue';
import { appCloudfrontPath } from '@constants';
import { getJobs } from '@api/team';
import { JobListing } from 'types/team';
import MarketingPageHeader from '@components/marketing-site/marketing-page-header.vue';
import { useHead } from '@unhead/vue';

const metaTitle = 'Careers';
const metaDescription =
	'Explore open positions at Fundrise. Join our team in building a better way to invest in institutional-quality, private-market investments.';
const accolades = [
	{
		imageUrl: '/images/marketing-site/careers_wapoTopWp.svg',
		label: 'Washington Post: Top Workplaces'
	},
	{
		imageUrl: '/images/marketing-site/careers_forbesFintech50.svg',
		label: 'Forbes: Fintech 50'
	},
	{
		imageUrl: '/images/marketing-site/careers_inc5000.svg',
		label: 'Inc: Inc 5000'
	},
	{
		imageUrl: '/images/marketing-site/careers_glassdoorRating.svg',
		label: 'Glassdoor: 5-star rating'
	}
];

const departments: Ref<Map<string, JobListing[]>> = ref(new Map<string, JobListing[]>());
const numberOfDepartments = ref(0);

const pageHeader: LandingPageHeader = {
	title: 'Careers',
	heading: 'Build the future of real estate investing with us',
	summary: [
		'We believe everyone deserves a simpler, smarter way to invest in real estate. So we wake up every morning laser focused on bringing that idea to life for hundreds of thousands of investors.'
	]
};

const leverLink: Link = {
	href: 'https://jobs.lever.co/fundrise',
	text: 'View current job openings'
};

async function setup(): Promise<void> {
	useHead({
		title: metaTitle,
		meta: [
			{ property: 'og:title', content: metaTitle },
			{ name: 'description', content: metaDescription },
			{ property: 'og:description', content: metaDescription }
		]
	});
	try {
		departments.value = await getJobs();
		numberOfDepartments.value = [...departments.value.entries()].length;
	} catch {}
}

setup();
</script>

<style lang="scss">
@use '../../styles/utilities/respond-to.scss' as *;

.marketing-site-careers {
	@include respond-to(lg) {
		padding-top: 3rem;
	}
	.image-overlap-section {
		z-index: 1;
		background: transparent;
		margin-bottom: -6rem;
		position: relative;
		&:after {
			content: '';
			width: 100%;
			height: 75%;
			background: white;
			position: absolute;
			top: 0;
			z-index: -1;
		}
	}

	.office-video {
		width: 100%;
	}
	.accolades {
		@include respond-to(md) {
			margin: 0 auto;
			max-width: 66.6666666667%;
		}
	}

	.accolade {
		margin: 0 0.5rem;
		max-width: 20%;

		@include respond-to(md) {
			max-width: 120px;
		}
	}
}
</style>
